import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import { Card, Button, Carousel, Badge, Row, Col } from "react-bootstrap";
import { FaHeart, FaStar, FaStarHalfAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import "../css/Accommodations.css";
import { formatRupiah } from "../utils/formatRupiah";

const CACHE_TTL_MS = 30 * 1000;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const getCacheKey = (params) =>
  `roomtypes_cache_${JSON.stringify(params || {})}`;

const fetchWithRetry = async (url, config = {}, maxRetries = 3) => {
  let attempt = 0;
  let lastError = null;
  while (attempt <= maxRetries) {
    try {
      const res = await axios.get(url, config);
      return res;
    } catch (err) {
      lastError = err;
      const status = err?.response?.status;
      if (status === 429 && attempt < maxRetries) {
        const retryAfterHeader = err?.response?.headers?.["retry-after"];
        const waitMs = retryAfterHeader
          ? Number(retryAfterHeader) * 1000
          : Math.pow(2, attempt) * 1000;
        await sleep(waitMs);
        attempt += 1;
        continue;
      }
      throw err;
    }
  }
  throw lastError;
};

const Accommodations = ({ searchParams }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [roomTypes, setRoomTypes] = useState([]);
  const [favorites, setFavorites] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_BASE = useMemo(
    () => (process.env.REACT_APP_APP_URL || "").replace(/\/$/, ""),
    [],
  );
  const HEADERS = useMemo(
    () => ({
      "X-Platform": process.env.REACT_APP_X_PLATFORM || "web",
      "API-Key": process.env.REACT_APP_API_KEY || "",
      "X-API-Version": process.env.REACT_APP_X_API_VERSION || "1",
    }),
    [],
  );

  const abortCtrlRef = useRef(null);
  const debounceTimerRef = useRef(null);

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("favorites")) || {};
      setFavorites(stored);
    } catch {
      setFavorites({});
    }
  }, []);

  const fetchRoomTypes = useCallback(
    async (params = {}) => {
      setError(null);
      setLoading(true);

      const cacheKey = getCacheKey(params);
      try {
        const cachedRaw = sessionStorage.getItem(cacheKey);
        if (cachedRaw) {
          const cached = JSON.parse(cachedRaw);
          if (cached && Date.now() - cached._ts < CACHE_TTL_MS) {
            setRoomTypes(cached.data || []);
            setLoading(false);
            return;
          }
        }
      } catch {}

      if (abortCtrlRef.current) {
        try {
          abortCtrlRef.current.abort();
        } catch {}
      }
      const controller = new AbortController();
      abortCtrlRef.current = controller;

      const url = `${API_BASE}/room-types`;
      try {
        const res = await fetchWithRetry(
          url,
          { headers: HEADERS, params, signal: controller.signal },
          3,
        );
        const items = Array.isArray(res?.data?.data) ? res.data.data : [];
        setRoomTypes(items);
        try {
          sessionStorage.setItem(
            cacheKey,
            JSON.stringify({ _ts: Date.now(), data: items }),
          );
        } catch {}
      } catch (err) {
        if (axios.isCancel(err) || err.name === "CanceledError") return;
        const status = err?.response?.status;
        if (status === 429) {
          setError(
            t("tooManyRequests") ||
              "Too many requests. Please try again later.",
          );
        } else {
          setError(
            err?.response?.data?.message ||
              t("errorFetchingData") ||
              "Failed to load data",
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [API_BASE, HEADERS, i18n.language],
  );

  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      fetchRoomTypes(searchParams || {});
    }, 400);
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [searchParams, fetchRoomTypes]);

  const favoriteCooldownRef = useRef({});
  const toggleFavorite = async (id) => {
    const token = localStorage.getItem("token");
    if (!token) return navigate("/login");

    if (
      favoriteCooldownRef.current[id] &&
      Date.now() - favoriteCooldownRef.current[id] < 800
    )
      return;
    favoriteCooldownRef.current[id] = Date.now();

    const isFav = !!favorites[id];
    const newFav = { ...favorites, [id]: !isFav };
    setFavorites(newFav);
    try {
      localStorage.setItem("favorites", JSON.stringify(newFav));
    } catch {}

    const url = `${API_BASE}/favorites${isFav ? `/${id}` : ""}`;
    try {
      if (isFav) {
        await axios.delete(url, {
          headers: { Authorization: `Bearer ${token}`, ...HEADERS },
        });
      } else {
        await axios.post(
          url,
          { room_type_id: id },
          { headers: { Authorization: `Bearer ${token}`, ...HEADERS } },
        );
      }
      Swal.fire({
        title: t("success") || "Success",
        text: isFav
          ? t("removedFavorite") || "Removed"
          : t("addedFavorite") || "Added",
        icon: "success",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (err) {
      const reverted = { ...favorites, [id]: isFav };
      setFavorites(reverted);
      try {
        localStorage.setItem("favorites", JSON.stringify(reverted));
      } catch {}
      const status = err?.response?.status;
      if (status === 429) {
        Swal.fire({
          title: t("error") || "Error",
          text: t("tooManyRequests") || "Too many requests. Try later.",
          icon: "error",
        });
      } else {
        Swal.fire({
          title: t("error") || "Error",
          text:
            err?.response?.data?.message ||
            t("failedUpdateFavorite") ||
            "Failed to update favorites",
          icon: "error",
        });
      }
    }
  };

  if (loading) return <LoadingSkeleton />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <section className="container mt-5">
      <Row className="g-4">
        {roomTypes.length > 0 ? (
          roomTypes.map((room) => {
            const id = String(room.room_type_id || room.id || "");
            const name = room.room_type_name || room.name || "-";
            const price = Number(room.price || 0);
            const photos = Array.isArray(room.room_photos)
              ? room.room_photos
              : [];
            const available = Number(room.available_rooms ?? 0);
            const thumbnail =
              room.thumbnail_url ||
              (photos[0] && photos[0].photo_url) ||
              `${process.env.PUBLIC_URL || ""}/assets/placeholder.jpg`;

            return (
              <Col key={id} xs={12} sm={6} md={4} lg={3}>
                <Card className="h-100 shadow-sm rounded-4 overflow-hidden position-relative">
                  <div className="position-relative">
                    {photos.length > 0 ? (
                      <Carousel
                        indicators={photos.length > 1}
                        controls={photos.length > 1}
                        interval={4000}
                        pause="hover"
                      >
                        {photos.map((p, idx) => (
                          <Carousel.Item key={`${p.id || p.photo_url}-${idx}`}>
                            <img
                              className="d-block w-100 card-img"
                              src={p.photo_url}
                              alt={`${name} ${idx + 1}`}
                              onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = `${process.env.PUBLIC_URL || ""}/assets/placeholder.jpg`;
                              }}
                              loading="lazy"
                            />
                          </Carousel.Item>
                        ))}
                      </Carousel>
                    ) : (
                      <Card.Img
                        variant="top"
                        src={thumbnail}
                        className="card-img"
                        alt={name}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = `${process.env.PUBLIC_URL || ""}/assets/placeholder.jpg`;
                        }}
                      />
                    )}

                    {available <= 0 && (
                      <Badge
                        bg="danger"
                        className="position-absolute top-0 start-0 m-2"
                      >
                        {t("soldOut") || "Sold out"}
                      </Badge>
                    )}

                    <Button
                      variant="light"
                      className="position-absolute top-0 end-0 m-2 p-2 rounded-circle shadow-sm heart-btn"
                      aria-label={
                        favorites[id] ? t("removeFavorite") : t("addFavorite")
                      }
                      onClick={() => toggleFavorite(id)}
                    >
                      <FaHeart
                        size={18}
                        className={favorites[id] ? "text-danger" : "text-muted"}
                      />
                    </Button>
                  </div>

                  <Card.Body className="d-flex flex-column">
                    <Card.Title className="fw-semibold mb-1">{name}</Card.Title>

                    <div className="d-flex align-items-center mb-2">
                      <RatingStars rating={Number(room.rating?.average ?? 0)} />
                      <small className="ms-2 text-muted">
                        {room.capacity || "-"} {t("guests") || "guests"}
                      </small>
                    </div>

                    <div className="mt-auto d-flex justify-content-between align-items-center">
                      <div>
                        {price > 0 ? (
                          <div className="fw-bold fs-6">
                            {formatRupiah(price)}
                          </div>
                        ) : (
                          <div className="fw-bold fs-6 text-muted">
                            {t("contactForPrice") || "Contact for price"}
                          </div>
                        )}
                        <small className="text-muted">
                          {room.currency || "IDR"}
                        </small>
                      </div>

                      <Button
                        variant="success"
                        onClick={() => navigate(`/detail-room/${id}`)}
                        disabled={available <= 0}
                      >
                        {t("bookNow") || "Book now"}
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            );
          })
        ) : (
          <p className="text-center mt-4">
            {t("noAccommodations") || "No accommodations found."}
          </p>
        )}
      </Row>
    </section>
  );
};

const LoadingSkeleton = () => (
  <div className="row">
    {[...Array(4)].map((_, i) => (
      <div className="col-md-3 col-sm-6 mb-4" key={`skeleton-${i}`}>
        <div className="card shimmer-card shadow-sm rounded-4 overflow-hidden">
          <div className="shimmer-img" style={{ height: 160 }} />
          <div className="p-3">
            <div className="shimmer-title mb-2" />
            <div className="shimmer-line" />
            <div className="shimmer-line" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

const ErrorMessage = ({ message }) => (
  <div className="container mt-4 text-center text-danger">
    <p>{message}</p>
  </div>
);

const RatingStars = ({ rating }) => {
  const fullStars = Math.floor(rating);
  const halfStar = rating - fullStars >= 0.5;
  const emptyStars = 5 - fullStars - (halfStar ? 1 : 0);
  return (
    <div className="d-flex align-items-center">
      {Array.from({ length: fullStars }).map((_, i) => (
        <FaStar key={`full-${i}`} className="text-warning me-1" />
      ))}
      {halfStar && <FaStarHalfAlt key="half" className="text-warning me-1" />}
      {Array.from({ length: emptyStars }).map((_, i) => (
        <FaStar key={`empty-${i}`} className="text-muted me-1" />
      ))}
    </div>
  );
};

Accommodations.propTypes = { searchParams: PropTypes.object };
ErrorMessage.propTypes = { message: PropTypes.string };

export default Accommodations;
